/**
 * Sala, who runs the circuit.
 *
 * He is not a fighter and is not on the ladder — `validateLadder` would refuse
 * him, and rightly, because there is no rank for a man who makes the card. He
 * is the campaign's *voice*: the three fights on the table are his, the purses
 * on them are his, and between them he is the one who says something about
 * where the player has got to.
 *
 * **He never lies and never threatens.** Every line is keyed on something that
 * is true at the moment he says it, so there is nothing for him to be wrong
 * about; and the worst thing that happens to a fighter he is not interested in
 * is that he is brief with them. There is no line in the file where he promises
 * a consequence, because a promoter does not need to — the ladder is the
 * consequence and he only reads it out.
 *
 * **The indifference is the character.** He is warm about a fighter who pulls
 * numbers: he uses their streak, he names the money, he tells them who is
 * watching. About a fighter who does not, he is unfailingly polite and says
 * almost nothing — *"Same time next week."* Nothing in here insults anybody.
 * That is what makes the warm lines worth getting.
 *
 * The selection is `lines.js`, the same first-match-wins ordering the trash
 * talk has used since step 52 — one copy of that rule, two scripts in front of
 * it. What is his is the facts he reads and the two screens he speaks on.
 *
 * Pure, and it holds no DOM: `src/ui` renders what this returns.
 */

import SCRIPT from './sala.json';
import { matches as matchesLine, pickLine as pick, validateLines } from './lines.js';
import { levelOf } from '../core/heat.js';

export class SalaConfigError extends Error {}

/** His name, in one place, because two screens print it. */
export const SALA = Object.freeze({ name: 'Sala', title: 'runs the circuit' });

/**
 * Where he says a thing. The field is `on` rather than `speaker` because there
 * is only one of him — what varies is which screen he is standing on, and a
 * file reading `"speaker": "aftermath"` would be describing the wrong thing.
 *
 * Each of them needs its own catch-all, which is the rule that matters: a
 * screen with a blank where his line should be is worse than a dull line.
 */
export const SCREENS = Object.freeze(['offer', 'aftermath']);

/**
 * Every fact a line of his may be keyed on.
 *
 * Narrower than the trash talk's on purpose. He is not in the fight and has no
 * opinion about anybody's style; what he has is a ledger, a door, and a rough
 * idea of how many people came. A `when` key that is not in here throws at
 * boot, because the only thing it can be is a typo — and a typo in a condition
 * is a line that silently never fires.
 */
export const FACTS = Object.freeze({
  /** The player's heat, and which band it falls in. */
  heat: 'number',
  room: 'text',
  /** Their run: positive for wins, negative for losses, zero before any. */
  streak: 'number',
  /** Where they are, and how far off the card. Zero gap means they are in. */
  rank: 'number',
  gap: 'number',
  contender: 'flag',
  /** Whether the card has already been won. */
  crowned: 'flag',
  /** Fights they have had. Zero is their first night. */
  fights: 'number',
  /** Reach cards turned down in a row, and whether that is costing them yet. */
  ducks: 'number',
  ducking: 'flag',
  /** The best money on the table tonight. */
  purse: 'number',
  /** Whether Junior is on the ladder, and how far behind. Negative: past them. */
  junior: 'flag',
  juniorGap: 'number',
  /** The last fight: `none` before there is one, else `win`, `loss` or `draw`. */
  result: 'text',
  /** And how it ended: `knockout`, `decision`, `draw`, or `none`. */
  by: 'text',
  /** Places the last result moved them. Positive is up. */
  moved: 'number',
  /** Whether the fight just recorded was the one for the card. */
  forTheCard: 'flag',
  /** Where the last fight was held, as a venue id, or `none`. */
  venue: 'text',
  /** Whether the room changed hands in it: `took`, `lost`, or `none`. */
  changed: 'text',
  /** How many rooms the player holds now. */
  holds: 'number',
  /** What the player's rooms paid out at their last fight, and which paid most. */
  takings: 'number',
  paidBy: 'text',
});

/**
 * What Sala knows, read off the campaign rather than passed around.
 *
 * Everything in here is something he could actually have seen — a result, a
 * rank, a gate, the takings, and whose name is over which door. He is handed
 * no fighter and no arena: a promoter who knew what the pit was doing would be
 * a second AI. The venue is not the arena — it is the booking, which is his.
 *
 * `event` is a recorded fight when the aftermath is asking, and null when the
 * offer screen is. Without one the last fight comes out of the history, which
 * is what lets the offer screen mention a result he has already commented on.
 */
export function readSala({ campaign, event = null } = {}) {
  const gate = campaign.gate;
  const last = event ?? campaign.history[0] ?? null;
  const duck = campaign.ducking;
  const debut = campaign.ladder.debut;
  const heat = campaign.heat;

  // The best card on the table. He set all three, and the one he would point at
  // is the one with the most money on it.
  const purse = campaign.offer.reduce((most, card) => Math.max(most, card.purse ?? 0), 0);

  return Object.freeze({
    heat: Math.round(heat),
    room: levelOf(heat),
    streak: campaign.streak,
    rank: gate.rank,
    gap: gate.short,
    contender: gate.open,
    crowned: campaign.actComplete,
    fights: campaign.fights,
    ducks: duck.count,
    ducking: duck.free === 0,
    purse,
    junior: Boolean(debut?.arrived),
    // How far behind the player he is. Negative once he is past them, and a
    // long way positive when he is not there at all, so a line asking whether
    // he is close cannot fire on a night he does not exist.
    juniorGap: debut?.arrived ? (debut.rank ?? 0) - gate.rank : Number.MAX_SAFE_INTEGER,
    result: last ? (last.drawn ? 'draw' : last.won ? 'win' : 'loss') : 'none',
    by: last?.by ?? 'none',
    moved: last ? (last.from ?? 0) - (last.rank ?? 0) : 0,
    forTheCard: Boolean(last?.act),
    venue: last?.venue?.id ?? 'none',
    changed: last?.venue?.changed ?? 'none',
    holds: campaign.ladder.player?.holds?.length ?? 0,
    takings: last?.takings?.total ?? 0,
    paidBy: richest(last?.takings),
  });
}

/** The room that paid the most in one collection, or `none`. Ties to the file's order. */
function richest(takings) {
  const rooms = takings?.rooms ?? [];
  if (!rooms.length) return 'none';
  return rooms.reduce((best, room) => (room.amount > best.amount ? room : best)).id;
}

/** Whether every condition on a line holds. A line with none always does. */
export function matches(line, facts) {
  return matchesLine(line, facts, FACTS);
}

/**
 * What he says on `screen`, given the state. Never null for a validated file:
 * each screen is guaranteed an unconditional line.
 */
export function salaLine(facts, { screen = 'offer', config = SCRIPT } = {}) {
  return pick(config.lines, facts, { voice: screen, field: 'on', table: FACTS });
}

/** The whole of him for one screen, which is what a screen actually wants. */
export function speak({ campaign, event = null, screen = 'offer', config = SCRIPT } = {}) {
  const facts = readSala({ campaign, event });
  return Object.freeze({ ...SALA, facts, line: salaLine(facts, { screen, config }) });
}

export function validateSala(config = SCRIPT) {
  return validateLines(config, {
    facts: FACTS,
    voices: SCREENS,
    field: 'on',
    what: 'Sala',
    Err: SalaConfigError,
  });
}

// A file that cannot always answer fails at boot rather than at a blank screen.
validateSala(SCRIPT);

export { SCRIPT };
